'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import { getDividends } from '@/lib/api-client';
import { getDividendBadgeWindow, pickDividendBadgeRows, type DividendSoon } from '@/lib/dividendBadge';

/**
 * Shared map of symbols to the ex-dividend date their blue D is about — any
 * ex-date from 1 trading day back through 2 trading days ahead (ET). One
 * SWR-deduped request of a few days of the dividend calendar, shared by every
 * watchlist row, popup and header, the same way useEarningsSoonSymbols feeds
 * the earnings badges.
 */
async function fetchDividendsSoon(): Promise<Array<[string, DividendSoon]>> {
  const { from, to } = getDividendBadgeWindow();
  const rows = await getDividends(from, to);
  return Array.from(pickDividendBadgeRows(
    (Array.isArray(rows) ? rows : []).map((r) => ({
      symbol: r.symbol,
      exDate: r.exDate,
      amount: r.amount,
      paymentDate: r.paymentDate,
      projected: r.projected === true,
    })),
  ).entries());
}

const EMPTY: ReadonlyMap<string, DividendSoon> = new Map();

export function useDividendSoonSymbols(): ReadonlyMap<string, DividendSoon> {
  const { data } = useSWR('dividends/soon-symbols', fetchDividendsSoon, {
    revalidateOnFocus: false,
    dedupingInterval: 5 * 60_000,
    refreshInterval: 30 * 60_000,
  });
  return useMemo(() => (data && data.length ? new Map(data) : EMPTY), [data]);
}
